import type {
  OverviewControlSummary,
  OverviewControlTopPattern,
} from './overviewTypes';
import type { OverviewControlSummaryResponse, OverviewControlTopPattern as ApiPattern } from '../../services/overviewApi';
import { displayControlStatusLabel } from './controlStatusDisplay';

const EMPTY: OverviewControlSummary = {
  criticalRisksUnderReview: 0,
  uncontrolledExposurePatterns: 0,
  exposurePatternsNeedingReview: 0,
  classificationAttentionCount: 0,
  qualifyingEntryCountTotal: 0,
  unassignedPatternOwners: 0,
  followupsDue: 0,
  followupActionGaps: 0,
  openWorkOrders: 0,
  linkedWorkOrdersOpen: 0,
  proofMediaLimitations: 0,
  evidenceProofGaps: 0,
  outcomeMeasurementsMissing: 0,
  topPatterns: [],
  classificationAttentionPatterns: [],
  loaded: false,
  error: null,
};

function ownerDisplay(value: string | null | undefined): string {
  const text = String(value || '').trim();
  return text || 'Not assigned';
}

function mapPatternRow(row: ApiPattern): OverviewControlTopPattern {
  const qualifyingEntryCount = Number(
    row.qualifying_entry_count ?? 0,
  );
  return {
    patternLabel: String(row.pattern_label || row.incident_type || 'Recurring hotspot'),
    incidentType: row.incident_type,
    cameraId: row.camera_id,
    deviceId: row.device_id,
    groupKey: row.group_key,
    zoneId: row.zone_id,
    patternVolumeCount: Number(row.pattern_volume_count ?? 0),
    qualifyingEntryCount,
    activeDaysInWindow: Number(row.active_days_in_window ?? 0),
    buyerSurface: row.buyer_surface,
    actionScopeCount: Number(row.action_scope_count ?? row.open_count ?? 0),
    severity: row.severity,
    controlStatus: String(row.control_status || 'unknown'),
    controlStatusLabel: displayControlStatusLabel(
      String(row.control_status || 'unknown'),
      row.control_status_label,
    ),
    patternOwner: ownerDisplay(row.pattern_owner),
    followupOwner: ownerDisplay(row.followup_owner),
    followupNextReviewAt: row.followup_next_review_at || null,
    evidenceStatus: String(row.evidence_status || 'Not evaluated'),
    outcomeMeasurementStatus: String(row.outcome_measurement_status || 'Not measured'),
    findingSaved: Boolean(row.finding_saved),
    reviewStatus: String(row.review_status || ''),
    correctiveActionStatus: String(row.corrective_action_status || ''),
    measurementStatus: String(row.measurement_status || ''),
    signoffStatus: String(row.signoff_status || ''),
    reportStatus: String(row.report_status || ''),
    finalizationStatus: String(row.finalization_status || ''),
    packageAvailable: Boolean(row.package_available),
    workOrderStatus: String(row.work_order_status || 'None'),
    linkToPatternReview: String(row.link_to_pattern_review || '/incidents?table_mode=grouped'),
    linkToSafetyReport: row.link_to_safety_report || null,
    riskPatternReviewUrl: row.risk_pattern_review_url || row.link_to_pattern_review || null,
    safetyCaseUrl: row.safety_case_url || null,
    safetyReportsUrl: row.safety_reports_url || null,
    safetyScanWorkspaceUrl: row.safety_scan_workspace_url || null,
    safetyCaseId: row.safety_case_id || null,
    findingId: row.finding_id || null,
    representativeIncidentId: row.representative_incident_id || null,
    latestSignedScanId: row.latest_signed_scan_id || null,
    latestSignedScanAt: row.latest_signed_scan_at || null,
    activeDraftReportId: row.active_draft_report_id || null,
    latestObservationAt: row.latest_observation_at || null,
    hasNewActivitySinceLatestSignedScan: row.has_new_activity_since_latest_signed_scan ?? null,
    followupOwnerExplicit: row.followup_owner_explicit ?? null,
    timing: row.timing
      ? {
          status: row.timing.status ?? null,
          windowLabel: row.timing.window_label ?? null,
          concentrationPercent: row.timing.concentration_percent ?? null,
          timezone: row.timing.timezone ?? null,
        }
      : null,
    scanMeasurementSummary: row.scan_measurement_summary
      ? {
          source: row.scan_measurement_summary.source ?? null,
          confirmedEpisodeCount:
            row.scan_measurement_summary.confirmed_episode_count ?? null,
          representativeEvidenceCount:
            row.scan_measurement_summary.representative_evidence_count ?? null,
          usableHours: row.scan_measurement_summary.usable_hours ?? null,
          measurementStatus:
            row.scan_measurement_summary.measurement_status ?? null,
          operationalFinding:
            row.scan_measurement_summary.operational_finding ?? null,
        }
      : null,
  };
}

function enrichWithSiteViewRow(
  row: ApiPattern,
  siteRows: ApiPattern[],
): ApiPattern {
  const groupKey = String(row.group_key || '').trim();
  const incidentType = String(row.incident_type || '').trim();
  const cameraId = String(row.camera_id || '').trim();
  const zoneId = String(row.zone_id || '').trim();
  const match = siteRows.find((candidate) => {
    if (groupKey && String(candidate.group_key || '').trim() === groupKey) return true;
    return Boolean(
      incidentType &&
      cameraId &&
        zoneId &&
        String(candidate.incident_type || '').trim() === incidentType &&
        String(candidate.camera_id || '').trim() === cameraId &&
        String(candidate.zone_id || '').trim() === zoneId,
    );
  });
  return match ? { ...row, ...match } : row;
}

export function mapOverviewControlSummaryResponse(
  response: OverviewControlSummaryResponse | null | undefined,
): OverviewControlSummary {
  if (!response?.ok || !response.control_summary) {
    return {
      ...EMPTY,
      loaded: Boolean(response),
      error: response?.ok === false ? 'Control summary unavailable' : null,
    };
  }
  const summary = response.control_summary;
  const siteRows = response.site_view?.valid_scan_register || [];
  const topPatterns = (summary.top_patterns || [])
    .map((row) => enrichWithSiteViewRow(row, siteRows))
    .map(mapPatternRow);
  const classificationAttentionPatterns = (summary.classification_attention_patterns || []).map(
    mapPatternRow,
  );
  const qualifyingEntryCountTotal = Number(
    summary.qualifying_entry_count_total
      ?? [...topPatterns, ...classificationAttentionPatterns].reduce(
        (total, row) => total + Number(row.qualifyingEntryCount || 0),
        0,
      ),
  );

  return {
    orgId: response.org_id,
    siteId: response.site_id,
    criticalRisksUnderReview: Number(summary.critical_risks_under_review ?? 0),
    uncontrolledExposurePatterns: Number(
      summary.uncontrolled_exposure_patterns
        ?? summary.exposure_patterns_needing_review
        ?? 0,
    ),
    exposurePatternsNeedingReview: Number(
      summary.exposure_patterns_needing_review ?? summary.uncontrolled_exposure_patterns ?? 0,
    ),
    classificationAttentionCount: Number(
      summary.classification_attention_count ?? classificationAttentionPatterns.length,
    ),
    signedSafetyControlScans: Number(
      response.site_view?.summary?.signed_safety_control_scans ??
        topPatterns.filter(
          (row) => row.scanMeasurementSummary?.source === 'signed_scan_report_v1',
        ).length,
    ),
    qualifyingEntryCountTotal,
    unassignedPatternOwners: Number(summary.unassigned_pattern_owners ?? 0),
    followupsDue: Number(summary.followups_due ?? 0),
    followupActionGaps: Number(summary.followup_action_gaps ?? summary.followups_due ?? 0),
    openWorkOrders: Number(summary.linked_work_orders_open ?? summary.open_work_orders ?? 0),
    linkedWorkOrdersOpen: Number(summary.linked_work_orders_open ?? summary.open_work_orders ?? 0),
    proofMediaLimitations: Number(summary.proof_media_limitations ?? 0),
    evidenceProofGaps: Number(
      summary.evidence_proof_gaps ?? summary.proof_media_limitations ?? 0,
    ),
    outcomeMeasurementsMissing: Number(summary.outcome_measurements_missing ?? 0),
    topPatterns,
    classificationAttentionPatterns,
    lookbackHours: Number(response.lookback_hours ?? 168),
    lookbackHoursLabel: String(response.lookback_hours_label || ''),
    asOf: response.as_of,
    loaded: true,
    error: null,
  };
}

export const emptyOverviewControlSummary = (): OverviewControlSummary => ({ ...EMPTY });

/** Buyer-facing volume for a pattern row (EPIC 263 qualifying entries). */
export function patternQualifyingEntryCount(row: OverviewControlTopPattern | null | undefined): number {
  return Math.max(0, Number(row?.qualifyingEntryCount || 0));
}
